/**
 * uploadToCdnClient.ts
 *
 * File-level:
 * - Basit bir frontend helper sağlar: backend'den presigned upload URL alır,
 *   dosyayı bu URL'e PUT ile gönderir ve publicUrl döner.
 *
 * Not:
 * - Backend'in /api/upload-url endpoint'inin aşağıdaki formatta JSON döndürmesi beklenir:
 *   { uploadUrl: string, publicUrl: string }
 *
 * - Eğer backend yoksa veya bir hata oluşursa fonksiyon bir hata fırlatır.
 */

 /**
  * UploadResponse
  *
  * Interface-level:
  * - Backend'den beklenen yanıt yapısını tanımlar.
  */
 export interface UploadResponse {
   /** Presigned (PUT/POST) upload URL (tarayıcı buraya upload yapacak) */
   uploadUrl: string
   /** CDN üzerinde erişilebilen public URL (kaydedilecek URL) */
   publicUrl: string
 }

 /**
  * uploadFileToCdn
  *
  * Function-level:
  * - Frontend tarafında bir dosyayı CDN'e yüklemek için kullanılan yardımcı fonksiyon.
  * - Adımlar:
  *   1) POST /api/upload-url ile presigned URL istenir (payload: filename, contentType, clientId).
  *   2) Dönen uploadUrl'e doğrudan PUT ile dosya gönderilir.
  *   3) publicUrl döndürülür.
  *
  * @param file File - yüklenmek istenen dosya
  * @param clientId string | undefined - isteğe bağlı client id (backend metadata için)
  * @returns Promise<string> publicUrl
  */
 export async function uploadFileToCdn(file: File, clientId?: string): Promise<string> {
   // Basit validasyon
   if (!file) throw new Error('No file provided')

   // 1) Presigned URL isteği
   const reqBody = {
     filename: file.name,
     contentType: file.type || 'application/octet-stream',
     clientId: clientId || undefined,
   }

   const res = await fetch('/api/upload-url', {
     method: 'POST',
     headers: {
       'Content-Type': 'application/json',
     },
     body: JSON.stringify(reqBody),
   })

   if (!res.ok) {
     const text = await res.text().catch(() => '')
     throw new Error(`Failed to obtain upload URL: ${res.status} ${text}`)
   }

   const json = (await res.json()) as UploadResponse

   if (!json?.uploadUrl) {
     throw new Error('Upload URL not returned from server')
   }

   // 2) Dosyayı presigned URL'e PUT et
   const uploadRes = await fetch(json.uploadUrl, {
     method: 'PUT',
     headers: {
       'Content-Type': file.type || 'application/octet-stream',
     },
     body: file,
   })

   if (!uploadRes.ok) {
     const text = await uploadRes.text().catch(() => '')
     throw new Error(`Upload failed: ${uploadRes.status} ${text}`)
   }

   // 3) publicUrl'i döndür
   if (!json.publicUrl) {
     // Eğer backend sadece uploadUrl verdiyse, bazı senaryolarda publicUrl oluşturulabilir.
     // Ancak burada backend'den publicUrl bekliyoruz.
     throw new Error('publicUrl not returned by server')
   }

   return json.publicUrl
 }